import { useState, useEffect, useCallback } from 'react';
import { storageApi } from '../services/storageApi';
import type {
  StorageProvider,
  StorageStatus,
  UnifiedFileItem,
  BreadcrumbItem,
  UploadProgress,
  DriveFile,
  OneDriveItem,
  ZohoItem,
} from '../types/storage';

// ─── useStorageStatus ─────────────────────────────────────────────────────────

export function useStorageStatus() {
  const [status, setStatus] = useState<StorageStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await storageApi.getStatus();
      setStatus(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { status, loading, error, refresh };
}

// ─── useFileExplorer ──────────────────────────────────────────────────────────

function normalizeGoogleFiles(files: DriveFile[]): UnifiedFileItem[] {
  return files.map((f) => ({
    id: f.id,
    name: f.name,
    mimeType: f.mimeType,
    size: f.size ? parseInt(f.size) : undefined,
    modifiedAt: f.modifiedTime,
    webUrl: f.webViewLink,
    isFolder: f.mimeType === 'application/vnd.google-apps.folder',
    provider: 'google' as StorageProvider,
  }));
}

function normalizeOneDriveFiles(items: OneDriveItem[]): UnifiedFileItem[] {
  return items.map((f) => ({
    id: f.id,
    name: f.name,
    mimeType: f.file?.mimeType,
    size: f.size,
    modifiedAt: f.lastModifiedDateTime,
    webUrl: f.webUrl,
    isFolder: !!f.folder,
    provider: 'onedrive' as StorageProvider,
  }));
}

function normalizeZohoFiles(items: ZohoItem[]): UnifiedFileItem[] {
  return items.map((f) => ({
    id: f.id,
    name: f.attributes.name,
    mimeType: f.attributes.type === 0 ? 'application/vnd.zoho-workdrive.folder' : undefined,
    size: f.attributes.size,
    modifiedAt: f.attributes.modified_time_in_millisecond
      ? new Date(f.attributes.modified_time_in_millisecond).toISOString()
      : undefined,
    webUrl: f.attributes.permalink,
    isFolder: f.attributes.type === 0,
    provider: 'zoho' as StorageProvider,
  }));
}

export function useFileExplorer(provider: StorageProvider) {
  const [items, setItems] = useState<UnifiedFileItem[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: provider === 'google' ? 'root' : 'root', name: 'My Drive' },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');

  const currentFolderId = breadcrumbs[breadcrumbs.length - 1]?.id ?? 'root';

  const loadFiles = useCallback(
    async (folderId: string, search?: string) => {
      setLoading(true);
      setError(null);
      try {
        if (provider === 'google') {
          const data = search
            ? await storageApi.google.searchFiles(search)
            : await storageApi.google.listFiles(folderId);
          setItems(normalizeGoogleFiles(data.files));
          setNextPageToken(data.nextPageToken);
        } else if (provider === 'zoho') {
          const data = search
            ? await storageApi.zoho.searchFiles(search)
            : await storageApi.zoho.listFiles(folderId);
          setItems(normalizeZohoFiles(data.data));
        } else {
          const data = search
            ? await storageApi.onedrive.listFiles('root', search)
            : await storageApi.onedrive.listFiles(folderId);
          setItems(normalizeOneDriveFiles(data.value));
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load files');
      } finally {
        setLoading(false);
      }
    },
    [provider]
  );

  useEffect(() => {
    // When searching, always search from root across all files
    const folderId = searchQuery ? 'root' : currentFolderId;
    loadFiles(folderId, searchQuery || undefined);
  }, [currentFolderId, searchQuery, loadFiles]);

  const openFolder = useCallback(
    (item: UnifiedFileItem) => {
      if (!item.isFolder) return;
      setBreadcrumbs((prev) => [...prev, { id: item.id, name: item.name }]);
    },
    []
  );

  const navigateTo = useCallback((index: number) => {
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
  }, []);

  const refresh = useCallback(() => {
    loadFiles(currentFolderId, searchQuery || undefined);
  }, [currentFolderId, searchQuery, loadFiles]);

  return {
    items,
    breadcrumbs,
    loading,
    error,
    nextPageToken,
    searchQuery,
    setSearchQuery,
    openFolder,
    navigateTo,
    refresh,
  };
}

// ─── useFileUpload ────────────────────────────────────────────────────────────

export function useFileUpload(provider: StorageProvider) {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);

  const updateUpload = (fileName: string, update: Partial<UploadProgress>) => {
    setUploads((prev) =>
      prev.map((u) => (u.fileName === fileName ? { ...u, ...update } : u))
    );
  };

  const uploadFiles = useCallback(
    async (files: File[], folderId?: string) => {
      const initial: UploadProgress[] = files.map((f) => ({
        fileName: f.name,
        progress: 0,
        status: 'pending',
      }));
      setUploads((prev) => [...prev, ...initial]);

      for (const file of files) {
        updateUpload(file.name, { status: 'uploading' });
        try {
          const onProgress = (p: number) => updateUpload(file.name, { progress: p });
          if (provider === 'google') {
            await storageApi.google.uploadFile(file, folderId, onProgress);
          } else if (provider === 'zoho') {
            await storageApi.zoho.uploadFile(file, folderId, onProgress);
          } else {
            await storageApi.onedrive.uploadFile(file, folderId, onProgress);
          }
          updateUpload(file.name, { status: 'done', progress: 100 });
        } catch (err: unknown) {
          updateUpload(file.name, {
            status: 'error',
            error: err instanceof Error ? err.message : 'Upload failed',
          });
        }
      }
    },
    [provider]
  );

  const clearCompleted = useCallback(() => {
    setUploads((prev) => prev.filter((u) => u.status !== 'done'));
  }, []);

  return { uploads, uploadFiles, clearCompleted };
}

// ─── useRecentFiles ───────────────────────────────────────────────────────────

export function useRecentFiles() {
  const [files, setFiles] = useState<UnifiedFileItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    storageApi
      .getRecentFiles()
      .then(setFiles)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return { files, loading };
}
